import { NgModule, ErrorHandler, ModuleWithProviders, Optional, SkipSelf } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';

import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { AccordionModule } from 'primeng/accordion';
import { PanelModule } from 'primeng/panel';
import { ToastModule } from 'primeng/toast';
import { ChipModule  } from 'primeng/chip';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { MessageModule } from 'primeng/message';
import { DropdownModule } from 'primeng/dropdown';
import { CardModule } from 'primeng/card';
import { SplitButtonModule } from 'primeng/splitbutton';
import { CalendarModule } from 'primeng/calendar';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from "primeng/dialog";

import { MessageService as PrimeNgMessageService } from 'primeng/api';

import { ConfirmationButtonComponent } from './components/confirmation-button/confirmation-button.component';
import { LocationSearchComponent } from './components/location-search/location-search.component';
import { FileImportComponent } from './components/file-import/file-import.component';
import { ThemeBreadcrumbComponent } from './theme/theme.breadcrumb.component';
import { ThemeComponent } from './theme/theme.component';
import { ThemeFooterComponent } from './theme/theme.footer.component';
import { ThemeMenuComponent, ThemeSubMenuComponent } from './theme/theme.menu.component';
import { ThemeTopBarComponent } from './theme/theme.topbar.component';
import { ErrorBlockComponent } from './components/error-block/error-block.component';
import { TimeEntryComponent } from './components/time-entry/time-entry.component';
import { ValidationMessageComponent } from './components/validation-message/validation-message.component';
import { PowerBiReportComponent } from './components/power-bi-report/power-bi-report.component';
import { WorkedCalendarComponent } from './components/worked-calendar/worked-calendar.component';
import { HistoryComponent } from './components/history/history.component';

import { VarDirective } from './directives/var.directive';
import { NumericDirective } from './directives/numeric.directive';
import { RangeDirective } from './directives/range.directive';

import { CamelCasePipe } from './pipes/camel-case.pipe';
import { EnumSelectPipe } from './pipes/enum-select.pipe';
import { EnumLabelPipe } from './pipes/enum-label.pipe';
import { MonthNamePipe } from './pipes/month-name.pipe';
import { MonthNameShortPipe } from './pipes/month-name-short.pipe';
import { TrustedUrlPipe } from './pipes/TrustUrl';

import { GlobalErrorHandler } from './services/global-error-handler';

import { SharedSettings } from './models/shared-settings';
import { BlobStorageComponent } from './components/blob-storage/blob-storage.component';
import { AlphanumericDirective } from './directives/alphanumeric.directive';
import { DateTimeToLocalTzPipe, DateTimeToTzPipe } from './pipes/datetime-to-local-tz.pipe';
import { MessageService } from './services/message-service';
import { ToastComponent } from './components/toast/toast.component';
import { TimeSincePipe } from './pipes/time-since.pipe';
import {TimeBetweenPipe} from "@shared/pipes/time-between.pipe";

@NgModule({
    declarations: [
        ConfirmationButtonComponent,
        LocationSearchComponent,
        ThemeBreadcrumbComponent,
        ThemeComponent,
        ThemeFooterComponent,
        ThemeMenuComponent,
        ThemeSubMenuComponent,
        ThemeTopBarComponent,
        ErrorBlockComponent,
        TimeEntryComponent,
        ToastComponent,
        ValidationMessageComponent,
        FileImportComponent,
        HistoryComponent,
        PowerBiReportComponent,
        WorkedCalendarComponent,

        CamelCasePipe,
        EnumLabelPipe,
        EnumSelectPipe,
        MonthNamePipe,
        MonthNameShortPipe,
        TrustedUrlPipe,
        TimeSincePipe,
        TimeBetweenPipe,

        VarDirective,
        NumericDirective,
        RangeDirective,
        BlobStorageComponent,
        AlphanumericDirective,
        DateTimeToLocalTzPipe,
        DateTimeToTzPipe,
        ToastComponent
    ],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        ButtonModule,
        MenuModule,
        BreadcrumbModule,
        HttpClientModule,
        ScrollPanelModule,
        AccordionModule,
        PanelModule,
        ToastModule,
        AutoCompleteModule,
        MessageModule,
        DropdownModule,
        ChipModule,
        InputTextModule,
        CardModule,
        SplitButtonModule,
        CalendarModule,
        DialogModule
    ],
    exports: [
        ConfirmationButtonComponent,
        LocationSearchComponent,
        ThemeComponent,
        CamelCasePipe,
        MonthNamePipe,
        MonthNameShortPipe,
        ToastComponent,
        EnumSelectPipe,
        EnumLabelPipe,
        TimeSincePipe,
        VarDirective,
        ErrorBlockComponent,
        TimeEntryComponent,
        NumericDirective,
        RangeDirective,
        ValidationMessageComponent,
        FileImportComponent,
        HistoryComponent,
        PowerBiReportComponent,
        TrustedUrlPipe,
        WorkedCalendarComponent,
        BlobStorageComponent,
        AlphanumericDirective,
        DateTimeToLocalTzPipe,
        DateTimeToTzPipe,
        ToastComponent,
        TimeBetweenPipe
    ]
})
export class SharedModule {
    constructor(@Optional() @SkipSelf() parentModule?: SharedModule) {
        if (parentModule) {
            throw new Error('`SharedModule` is already loaded.');
        }
    }

    static forRoot(settings: SharedSettings): ModuleWithProviders<SharedModule> {
        return {
            ngModule: SharedModule,
            providers: [
                { provide: SharedSettings, useValue: settings },
                { provide: ErrorHandler, useClass: GlobalErrorHandler },
                {
                    provide: PrimeNgMessageService,
                    useExisting: MessageService,
                },
            ]
        };
    }
}
